// src/components/ModalPago.js
// Web: MercadoPago (COP/ARS/MXN/CLP/PEN/BRL) + PayPal (USD)
// Android (Play Store): @capgo/native-purchases
import React, { useState, useEffect } from 'react';  // ✅
import { PLAN_PRICES, CURRENCIES } from '../utils/constants';
import { useTranslation } from 'react-i18next';

const isNativeAndroid = () => {
  try { return !!(window.Capacitor && window.Capacitor.isNativePlatform()); }
  catch(e) { return false; }
};

const PLAY_PRODUCT_IDS = {
  starter: 'pitazo_plan_starter', grupo: 'pitazo_plan_grupo',
  empresa: 'pitazo_plan_empresa', grande: 'pitazo_plan_grande', masivo: 'pitazo_plan_masivo',
};

const PLAY_PRICES = {
  starter: '$6 USD', grupo: '$9 USD', empresa: '$13 USD', grande: '$21 USD', masivo: '$35 USD',
};

const C = {
  bg: '#080810', card: '#14141f', gold: '#F0B429', goldDark: '#C9820A',
  green: '#06B86A', red: '#E63946', border: 'rgba(255,255,255,0.07)', paypal: '#0070E0',
};

const FUNCTIONS_BASE = 'https://us-central1-pitazo-mundial.cloudfunctions.net';

export const PLAN_NAMES = {
  starter: 'Starter', grupo: 'Grupo', empresa: 'Empresa', grande: 'Grande', masivo: 'Masivo',
};

const PAYMENT_METHODS = {
  COP: [{ icon: '💳', label: 'Tarjeta débito/crédito' }, { icon: '🏦', label: 'PSE' }, { icon: '📱', label: 'Nequi' }, { icon: '💜', label: 'Daviplata' }, { icon: '🏪', label: 'Efecty' }],
  ARS: [{ icon: '💳', label: 'Tarjeta débito/crédito' }, { icon: '📱', label: 'Mercado Pago' }, { icon: '🏪', label: 'Rapipago' }, { icon: '🏪', label: 'Pago Fácil' }],
  MXN: [{ icon: '💳', label: 'Tarjeta débito/crédito' }, { icon: '🏪', label: 'OXXO' }, { icon: '🏦', label: 'SPEI' }, { icon: '📱', label: 'Mercado Pago' }],
  CLP: [{ icon: '💳', label: 'Tarjeta débito/crédito' }, { icon: '🏦', label: 'Khipu' }, { icon: '🏦', label: 'Webpay' }, { icon: '📱', label: 'Mercado Pago' }],
  PEN: [{ icon: '💳', label: 'Tarjeta débito/crédito' }, { icon: '📱', label: 'Yape' }, { icon: '🏦', label: 'PagoEfectivo' }],
  BRL: [{ icon: '💳', label: 'Tarjeta débito/crédito' }, { icon: '⚡', label: 'Pix' }, { icon: '🧾', label: 'Boleto Bancário' }],
  USD: [{ icon: '💳', label: 'Tarjeta débito/crédito' }, { icon: '🔵', label: 'Saldo PayPal' }, { icon: '🏦', label: 'Transferencia bancaria' }],
};

const COUNTRY_NAME = { COP: 'Colombia', ARS: 'Argentina', MXN: 'México', CLP: 'Chile', PEN: 'Perú', BRL: 'Brasil' };

export default function ModalPago({ plan, groupId, userId, currency = 'COP', onClose, onSuccess }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (isNativeAndroid()) {
    return <ModalPagoPlayStore plan={plan} groupId={groupId} userId={userId} onClose={onClose} onSuccess={onSuccess} />;
  }

  const selectedCurrency = CURRENCIES.find(c => c.code === currency);
  const price = PLAN_PRICES[currency]?.[plan?.id];
  const planName = PLAN_NAMES[plan?.id];
  const isPayPal = selectedCurrency?.provider === 'paypal';
  const methods = PAYMENT_METHODS[currency] || PAYMENT_METHODS['USD'];

  async function handleMercadoPago() {
    setLoading('mp'); setError(null);
    try {
      const res = await fetch(`${FUNCTIONS_BASE}/crearPagoMP`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId: plan.id, planName, price, currency, groupId, userId }),
      });
      const data = await res.json();
      if (data.url) { window.open(data.url, '_blank'); onSuccess && onSuccess(); }
      else setError(t('modal_error_mp'));
    } catch { setError(t('modal_error_connection')); }
    setLoading(false);
  }

  async function handlePayPal() {
    setLoading('pp'); setError(null);
    try {
      const res = await fetch(`${FUNCTIONS_BASE}/crearPagoPayPal`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId: plan.id, planName, price, groupId, userId }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else setError(t('modal_error_paypal'));
    } catch { setError(t('modal_error_connection')); }
    setLoading(false);
  }

  if (!plan) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: C.card, borderRadius: 20, border: `1px solid ${C.border}`, padding: '2rem', maxWidth: 400, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>⚽</div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1.4rem', fontWeight: 700, letterSpacing: 2, color: '#F5F5F0', marginBottom: '0.3rem' }}>
            Plan <span style={{ color: C.gold }}>{planName}</span>
          </div>
          <div style={{ fontSize: '0.85rem', color: '#6B6B7B' }}>{t('modal_up_to', { count: plan.maxMembers })}</div>
        </div>

        <div style={{ background: isPayPal ? 'rgba(0,112,224,0.08)' : 'rgba(240,180,41,0.08)', border: `1px solid ${isPayPal ? 'rgba(0,112,224,0.2)' : 'rgba(240,180,41,0.2)'}`, borderRadius: 12, padding: '1rem', textAlign: 'center', marginBottom: '0.75rem' }}>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: '2rem', fontWeight: 700, color: isPayPal ? C.paypal : C.gold }}>
            {selectedCurrency?.symbol}{price?.toLocaleString('es-CO')} {currency}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6B6B7B', marginTop: 4 }}>{t('modal_single_payment')}</div>
        </div>

        <div style={{ background: 'rgba(0,0,0,0.25)', border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px', marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.6rem', color: '#6B6B7B', letterSpacing: 1, textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            {isPayPal ? t('modal_payment_methods_paypal') : t('modal_payment_methods_local', { country: COUNTRY_NAME[currency] || '' })}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 14px' }}>
            {methods.map((m, i) => (
              <div key={i} style={{ fontSize: '0.72rem', color: '#9999aa', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span>{m.icon}</span> {m.label}
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div style={{ background: 'rgba(230,57,70,0.1)', border: '1px solid rgba(230,57,70,0.2)', borderRadius: 10, padding: '0.75rem', color: '#E63946', fontSize: '0.8rem', textAlign: 'center', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {!isPayPal ? (
          <button onClick={handleMercadoPago} disabled={!!loading}
            style={{ width: '100%', padding: '14px', background: loading === 'mp' ? 'rgba(240,180,41,0.3)' : `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`, border: 'none', borderRadius: 12, fontFamily: "'Oswald', sans-serif", fontSize: '1rem', fontWeight: 700, letterSpacing: 2, color: '#080810', cursor: loading ? 'not-allowed' : 'pointer', marginBottom: '0.75rem', transition: 'all 0.2s' }}>
            {loading === 'mp' ? t('modal_generating') : `🟡 ${t('btn_pay_mp')}`}
          </button>
        ) : (
          <button onClick={handlePayPal} disabled={!!loading}
            style={{ width: '100%', padding: '14px', background: loading === 'pp' ? 'rgba(0,112,224,0.3)' : `linear-gradient(135deg, #0070E0, #003087)`, border: 'none', borderRadius: 12, fontFamily: "'Oswald', sans-serif", fontSize: '1rem', fontWeight: 700, letterSpacing: 2, color: '#fff', cursor: loading ? 'not-allowed' : 'pointer', marginBottom: '0.75rem', transition: 'all 0.2s' }}>
            {loading === 'pp' ? t('modal_redirecting') : `🔵 ${t('btn_pay_paypal')}`}
          </button>
        )}

        <button onClick={onClose}
          style={{ width: '100%', padding: '12px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 12, fontFamily: "'Inter', sans-serif", fontSize: '0.85rem', color: '#6B6B7B', cursor: 'pointer' }}>
          {t('modal_cancel')}
        </button>

        <div style={{ fontSize: '0.6rem', color: '#444', textAlign: 'center', marginTop: '1rem' }}>
          {isPayPal ? t('modal_secure_paypal') : t('modal_secure_mp')}
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// MODAL GOOGLE PLAY — API REAL de @capgo/native-purchases
// ═══════════════════════════════════════════════════════════════════
function ModalPagoPlayStore({ plan, groupId, userId, onClose, onSuccess }) {
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  // Resetear estado cuando cambia el plan (útil para "mejorar plan")
useEffect(() => {
  setStatus('idle');
  setErrorMsg('');
}, [plan?.id]);

  const productId    = PLAY_PRODUCT_IDS[plan?.id];
  const displayPrice = PLAY_PRICES[plan?.id] || '';

  // Al abrir el modal, revisar compras pendientes
  useEffect(() => {
    checkPendingPurchase();
  }, [productId]);

  async function checkPendingPurchase() {
    try {
      const { NativePurchases } = window.Capacitor?.Plugins || {};
      if (!NativePurchases || !productId) return;

      // 2. Después hay que llamar getPurchases() para obtener las compras
      const result = await NativePurchases.getPurchases({
        productType: 'inapp', // o PURCHASE_TYPE.INAPP si lo importaste
      });
      const purchases = result?.purchases || [];

      // 3. Buscar compra de este producto no consumida
      const pending = purchases.find(p =>
        p.productIdentifier === productId &&
        p.purchaseState === '1' && // "1" = PURCHASED en Android
        !p.isConsumed
      );

      if (pending) {
        const token = pending.purchaseToken || pending.transactionId;
        if (token) {
          setStatus('loading');
          try {
            await finalizePurchase(token, productId);
          } catch(e) {
            // Si falla la finalización, volver a idle para que el botón siga visible
            console.log('checkPendingPurchase finalize failed, keeping idle:', e?.message);
            setStatus('idle');
          }
        }
      }
    } catch (e) {
      console.log('checkPendingPurchase:', e?.message);
    }
  }

  // Activar plan directamente en Firestore + intentar verificar en servidor
  async function finalizePurchase(purchaseToken, purchasedProductId) {
    try {
      const planId = plan?.id || (purchasedProductId || productId).replace('pitazo_plan_', '');

      // 1. Activar el grupo DIRECTAMENTE en Firestore (no depende del servidor)
      const { updateDoc, doc: fsDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../firebase');

      if (groupId) {
        await updateDoc(fsDoc(db, 'groups', groupId), {
          status: 'active',
          plan: planId,
          paidAt: serverTimestamp(),
          paymentMethod: 'google_play',
          purchaseToken: purchaseToken || 'unknown',
        });
      }
      if (userId) {
        await updateDoc(fsDoc(db, 'users', userId), {
          hasActivePaidGroup: true,
        });
      }

      // 2. Intentar consumir el producto para que pueda comprarse de nuevo
      try {
        const { NativePurchases } = window.Capacitor?.Plugins || {};
        if (NativePurchases && purchaseToken) {
          await NativePurchases.consumePurchase({ purchaseToken });
        }
      } catch(consumeErr) {
        console.warn('consume no disponible:', consumeErr?.message);
      }

      // 3. Notificar al servidor en segundo plano (no crítico)
      fetch(`${FUNCTIONS_BASE}/verifyGooglePlayPurchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          purchaseToken: purchaseToken || 'unknown',
          productId: purchasedProductId || productId,
          groupId,
          userId,
        }),
      }).catch(e => console.warn('Server verify failed (no crítico):', e?.message));

      setStatus('success');
      // FIX: guardar el grupo activo para que /admin lo cargue correctamente
      if (groupId) localStorage.setItem('activeGroupId', groupId);
      setTimeout(() => { onSuccess?.(); }, 1800);

    } catch (err) {
      console.error('finalizePurchase error:', err);
      setErrorMsg(err.message || 'Error activando el plan');
      setStatus('error');
      throw err; // re-lanzar para que el llamador pueda sobrescribir el status
    }
  }

  // Recuperar compra manualmente
  async function recuperarCompra() {
  setStatus('loading');
  try {
    const { NativePurchases } = window.Capacitor?.Plugins || {};
    if (!NativePurchases) throw new Error('Plugin no disponible');

    // Forzar sincronización con Google Play
    await NativePurchases.restorePurchases();

    const result = await NativePurchases.getPurchases({ productType: 'inapp' });
    const purchases = result?.purchases || [];

    // Buscar cualquier compra de este producto que no esté consumida
    const existing = purchases.find(p =>
      p.productIdentifier === productId &&
      p.purchaseState === '1' &&
      !p.isConsumed
    );

    if (existing) {
      const token = existing.purchaseToken || existing.transactionId;
      if (token) {
        try {
          await finalizePurchase(token, productId);
          return;
        } catch(e) {
          console.log('recuperarCompra finalize failed:', e?.message);
          setErrorMsg('Compra encontrada pero no se pudo activar. Intenta de nuevo o contacta soporte.');
          setStatus('error');
          return;
        }
      }
    }
    setErrorMsg('No se encontró compra pendiente. Si ya pagaste, contacta al soporte.');
    setStatus('error');
  } catch (e) {
    setErrorMsg(e.message || 'Error recuperando la compra');
    setStatus('error');
  }
}

  async function handleGooglePlayPurchase() {
    if (!productId) {
      setErrorMsg('Producto no configurado.');
      setStatus('error');
      return;
    }
    setStatus('loading');
    try {
      const { NativePurchases } = window.Capacitor.Plugins;
      if (!NativePurchases) throw new Error('Plugin no disponible');

      const { isBillingSupported } = await NativePurchases.isBillingSupported();
      if (!isBillingSupported) throw new Error('Pagos no soportados');

      // purchaseProduct devuelve Transaction directamente
      const transaction = await NativePurchases.purchaseProduct({
        productIdentifier: productId,
        productType: 'inapp',
        quantity: 1,
      });

      const purchaseToken = transaction?.purchaseToken || transaction?.transactionId || null;
      if (!purchaseToken) throw new Error('No se recibió token');

      await finalizePurchase(purchaseToken, transaction?.productIdentifier || productId);

    } catch (err) {
      const code = err?.code || '';
      const msg  = err?.message || '';

      if (code === 'USER_CANCELLED' || msg.toLowerCase().includes('cancel')) {
        setStatus('idle');
        return;
      }

      if (code === 'ITEM_ALREADY_OWNED' || msg.toLowerCase().includes('already owned')) {
  setTimeout(() => recuperarCompra(), 500);
  return;
}

      setErrorMsg(msg || 'Error procesando el pago');
      setStatus('error');
    }
  }

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const modal = { background: '#14141f', borderRadius: '20px', padding: '28px 20px 40px', width: 'calc(100% - 32px)', maxWidth: 440, margin: '16px', border: '1px solid rgba(255,255,255,0.08)' };

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 18, letterSpacing: 1 }}>
            ⚡ {plan?.name?.toUpperCase() || 'PLAN'}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6B6B7B', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        {status === 'success' ? (
          <div style={{ textAlign: 'center', padding: '30px 0' }}>
            <div style={{ fontSize: 52, marginBottom: 12 }}>🎉</div>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 20, color: '#06B86A', letterSpacing: 1 }}>¡PLAN ACTIVADO!</div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#6B6B7B', marginTop: 8 }}>
              Tu grupo ahora tiene {plan?.maxMembers} cupos
            </div>
          </div>
        ) : (
          <>
            <div style={{ background: 'rgba(240,180,41,0.06)', border: '1px solid rgba(240,180,41,0.15)', borderRadius: 12, padding: '16px', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#F5F5F0' }}>
                  Hasta {plan?.maxMembers} participantes
                </span>
                <span style={{ fontFamily: "'Oswald',sans-serif", fontSize: 22, color: '#F0B429' }}>{displayPrice}</span>
              </div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#6B6B7B' }}>
                Pago único · Procesado por Google Play
              </div>
            </div>

            {status === 'error' && (
              <div style={{ background: 'rgba(230,57,70,0.08)', border: '1px solid rgba(230,57,70,0.2)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
                <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: '#E63946', marginBottom: 10 }}>
                  ⚠️ {errorMsg}
                </div>
                <button onClick={recuperarCompra}
                  style={{ width: '100%', padding: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontFamily: "'Inter',sans-serif", fontSize: 12, color: '#aaa', cursor: 'pointer' }}>
                  ♻️ Recuperar compra anterior
                </button>
              </div>
            )}

            {/* Botón siempre visible — incluso cuando hay error, para poder reintentar */}
            <button onClick={() => { setStatus('idle'); setErrorMsg(''); setTimeout(handleGooglePlayPurchase, 100); }} disabled={status === 'loading'}
              style={{ width: '100%', padding: '16px', borderRadius: 12, border: 'none', cursor: status === 'loading' ? 'not-allowed' : 'pointer', background: status === 'loading' ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, #34A853, #1a7a32)', fontFamily: "'Oswald',sans-serif", fontSize: 15, letterSpacing: 1, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, opacity: status === 'loading' ? 0.7 : 1, marginBottom: 8 }}>
              {status === 'loading' ? (
                <>
                  <span style={{ width: 18, height: 18, border: '3px solid rgba(255,255,255,0.2)', borderTop: '3px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite', display: 'inline-block' }} />
                  Procesando...
                </>
              ) : status === 'error' ? '🔄 INTENTAR DE NUEVO' : 'COMPRAR EN GOOGLE PLAY'}
            </button>

            <div style={{ textAlign: 'center', marginTop: 12, fontFamily: "'Inter',sans-serif", fontSize: 10, color: '#444' }}>
              🔒 Pago seguro procesado por Google Play
            </div>
          </>
        )}
      </div>
    </div>
  );
}